'use client';

import { useMemo, useRef, useState } from 'react';
import { consumeToolUsage } from '@/lib/browser-tool-usage';
import { computeOvulation } from '@/lib/browser-healthfin-tools';

export default function BrowserOvulationCalculator({
  config,
}: {
  config?: { toolSlug?: string; heading?: string };
}) {
  const reported = useRef(false);
  const reportUse = () => {
    if (reported.current) return;
    reported.current = true;
    void consumeToolUsage(config?.toolSlug ?? 'ovulation-calculator', '').catch(() => undefined);
  };
  const [lmp, setLmp] = useState('');
  const [cycleLength, setCycleLength] = useState('28');

  const result = useMemo(() => computeOvulation(lmp, cycleLength), [lmp, cycleLength]);

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Ovulation</span>
        <h2 className="tool-live-title">
          {config?.heading ?? 'Ovulation & Fertile Window Calculator'}
        </h2>
        <p className="tool-live-subtitle">
          Enter the first day of your last period and your average cycle length to estimate your
          fertile window, ovulation day, and next period. All calculations run in your browser.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field tool-live-field-full">
              <span>First day of last period</span>
              <input
                onChange={(event) => {
                  reportUse();
                  setLmp(event.target.value);
                }}
                type="date"
                value={lmp}
              />
            </label>
            <label className="tool-live-field tool-live-field-full">
              <span>Average cycle length (days)</span>
              <input
                inputMode="numeric"
                onChange={(event) => {
                  reportUse();
                  setCycleLength(event.target.value);
                }}
                placeholder="28"
                type="text"
                value={cycleLength}
              />
            </label>
          </div>
          <p className="tool-live-panel-note">
            This is an estimate for general information only and is not medical advice or a form of
            contraception.
          </p>
        </section>

        <section className="tool-live-preview">
          {!result.ok ? (
            <p className="status-message" style={{ color: '#b91c1c' }}>
              {result.message}
            </p>
          ) : (
            <>
              <div className="tool-html-meta-grid">
                <div className="tool-live-field">
                  <span>Estimated ovulation day</span>
                  <input readOnly type="text" value={result.ovulationDate ?? ''} />
                </div>
                <div className="tool-live-field">
                  <span>Fertile window opens</span>
                  <input readOnly type="text" value={result.fertileStart ?? ''} />
                </div>
                <div className="tool-live-field">
                  <span>Fertile window closes</span>
                  <input readOnly type="text" value={result.fertileEnd ?? ''} />
                </div>
                <div className="tool-live-field">
                  <span>Next period expected</span>
                  <input readOnly type="text" value={result.nextPeriod ?? ''} />
                </div>
              </div>
              <p className="tool-live-panel-note">
                Ovulation is estimated about 14 days before your next period. The fertile window
                spans the five days before ovulation through the day after.
              </p>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
